import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Icon,
  useDisclosure,
  Portal,
  Tooltip,
} from '@wegroup/design-system';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { useLocale } from '../../../../../../../../i18n';
import usePartyId from '../../../../../../../common/src/hooks/query-params/usePartyId';
import useTracking from '../../../../../../../common/src/hooks/tracking/useTracking';
import useCompanyScanFlowId from '../../../../../../../company-scan/hooks/useCompanyScanFlowId';
import { useParty } from '../../../../../../../parties/src/features/detail/api/getParty';
import ShareCompanyScanConversationModal from '../../../../../../../risk-analysis/src/pages/recommendations/ShareCompanyScanConversationModal';
import usePreviewMode from '../../../../../hooks/useFlowsPreviewMode';
import { useAskLaterQuestions } from '../ask-later-questions/api/getAskLaterQuestion';

const ViewOnlyModeActionMenu: React.FC = ({ ...otherProps }) => {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const { isOpen, onClose, onOpen } = useDisclosure();
  const partyId = usePartyId();
  const { data: party } = useParty(partyId);
  const { userLang } = useLocale();
  const { toggle, isPreviewModeOn, isDisabled } = usePreviewMode(partyId);
  const { trackEvent } = useTracking();
  const flowId = useCompanyScanFlowId();
  const { data: askLater, isLoading: isLoadingAskLater } =
    useAskLaterQuestions();

  const handleClickShare = () => {
    trackEvent('Clicks share link on the view mode header for company scan');
    onOpen();
  };

  const handleClickPreviewMode = () => {
    if (partyId) toggle();
  };

  return (
    <>
      {party && (
        <ShareCompanyScanConversationModal
          isOpen={isOpen}
          onClose={onClose}
          partyId={party.id}
          partyLang={party.native_language || userLang}
        />
      )}

      <Menu {...otherProps}>
        <MenuButton
          as={Button}
          rightIcon={<Icon name="GeneralActionsChevronDown" />}
          variant="ghost"
          size="sm"
          isLoading={isLoadingAskLater}
        >
          {t('ACTIONS')}
        </MenuButton>
        <Portal>
          <MenuList>
            <MenuItem
              onClick={handleClickPreviewMode}
              icon={<Icon name="GeneralActionsEyeClosed" />}
              isDisabled={isDisabled && !isPreviewModeOn}
            >
              {isPreviewModeOn
                ? t('FLOWS_COMPANY-SCAN_PREVIEW-HEADER_TURN-OFF')
                : t('FLOWS_COMPANY-SCAN_PREVIEW-HEADER_TURN-ON')}
            </MenuItem>
            <Tooltip
              label={t('SHARE-CONVERSATION_TOOLTIP_DISABLED')}
              isDisabled={askLater?.questions.length !== 0}
            >
              <MenuItem
                onClick={handleClickShare}
                icon={<Icon name="GeneralActionsLink" />}
                isDisabled={askLater?.questions.length === 0}
              >
                {t('SHARE-CONVERSATION')}
              </MenuItem>
            </Tooltip>
            <Tooltip
              label={t('SHARE-CONVERSATION_TOOLTIP_DISABLED')}
              isDisabled={askLater?.questions.length !== 0}
            >
              <MenuItem
                as={Link}
                to={`/conversation/print/${flowId}?partyId=${partyId}&is_ask_later=1&backRef=${
                  pathname + search
                }`}
                icon={<Icon name="GeneralActionsPrint" />}
                onClick={() => trackEvent('Print conversation for ask later')}
                isDisabled={askLater?.questions.length === 0}
              >
                {t('DASHBOARD_CONVERSATION_BUTTON_PRINT-CONVERSATION')}
              </MenuItem>
            </Tooltip>
          </MenuList>
        </Portal>
      </Menu>
    </>
  );
};

export default ViewOnlyModeActionMenu;
